import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  brandOnboard,
  brandAutoDetectDomains,
  brandGetFullDetails,
  brandUpdateFullDetails,
  citationsAddOwnedDomain,
} from "../services/api";

// ---------------------------------------------------------------------------
// Theme constants
// ---------------------------------------------------------------------------
const GOLD = "#FFCC33";
const GOLD_HOVER = "#e6b800";
const NAVY = "#1a1f36";
const BORDER = "#e2e5ea";
const CHIP_BG = "#e8f0fe";
const CHIP_TEXT = "#1a56db";
const CHIP_X = "#6b7280";
const STEP_INACTIVE = "#d1d5db";
const STEP_LINE = "#d1d5db";
const ERROR_RED = "#dc2626";
const BADGE_GREEN = "#dcfce7";
const BADGE_GREEN_TEXT = "#166534";
const BADGE_PURPLE = "#ede9fe";
const BADGE_PURPLE_TEXT = "#5b21b6";

const TOTAL_STEPS = 4;
const STEP_LABELS = [
  "Brand Identity",
  "Brand Hierarchy",
  "Competition",
  "Owned Domains",
];

// ---------------------------------------------------------------------------
// Shared inline styles
// ---------------------------------------------------------------------------
const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(15, 23, 42, 0.45)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
};

const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  width: "100%",
  maxWidth: 720,
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 24px 48px rgba(15,23,42,0.18), 0 8px 16px rgba(15,23,42,0.08)",
  overflow: "hidden",
  position: "relative",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  fontSize: 14,
  border: `1px solid ${BORDER}`,
  borderRadius: 10,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
  fontFamily: "inherit",
};

const inputFocusColor = GOLD;

const btnBase = {
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: 700,
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  transition: "background 0.15s, opacity 0.15s",
  fontFamily: "inherit",
};

const btnPrimary = {
  ...btnBase,
  background: GOLD,
  color: NAVY,
};

const btnSecondary = {
  ...btnBase,
  background: "#f3f4f6",
  color: "#374151",
};

const btnGhost = {
  ...btnBase,
  background: "transparent",
  color: "#6b7280",
  textDecoration: "underline",
  padding: "10px 16px",
};

// ---------------------------------------------------------------------------
// Tiny reusable sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ current }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0,
        padding: "24px 32px 16px",
      }}
    >
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const isActive = i <= current;
        const isCurrent = i === current;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <div
                style={{
                  flex: 1,
                  maxWidth: 64,
                  height: 2,
                  background: i <= current ? GOLD : STEP_LINE,
                  transition: "background 0.25s",
                }}
              />
            )}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 800,
                  color: isActive ? NAVY : "#9ca3af",
                  background: isActive ? GOLD : "#f3f4f6",
                  border: isCurrent ? `2px solid ${NAVY}` : "2px solid transparent",
                  transition: "all 0.25s",
                }}
              >
                {i + 1}
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCurrent ? NAVY : "#9ca3af",
                  whiteSpace: "nowrap",
                }}
              >
                {STEP_LABELS[i]}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FocusInput({ style, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputStyle,
        ...style,
        borderColor: focused ? inputFocusColor : BORDER,
        boxShadow: focused ? `0 0 0 3px ${GOLD}33` : "none",
      }}
      onFocus={(e) => {
        setFocused(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        props.onBlur?.(e);
      }}
    />
  );
}

/** Multi-input: type + Enter to add chips. */
function ChipInput({ values, onChange, placeholder }) {
  const [text, setText] = useState("");

  const addChip = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setText("");
      return;
    }
    onChange([...values, trimmed]);
    setText("");
  }, [text, values, onChange]);

  const removeChip = useCallback(
    (idx) => {
      onChange(values.filter((_, i) => i !== idx));
    },
    [values, onChange]
  );

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: values.length ? 8 : 0 }}>
        {values.map((v, i) => (
          <span
            key={i}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: CHIP_BG,
              color: CHIP_TEXT,
              fontSize: 13,
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: 8,
            }}
          >
            {v}
            <button
              type="button"
              onClick={() => removeChip(i)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: CHIP_X,
                fontSize: 15,
                lineHeight: 1,
                padding: 0,
                marginLeft: 2,
                fontWeight: 700,
              }}
              aria-label={`Remove ${v}`}
            >
              &times;
            </button>
          </span>
        ))}
      </div>
      <FocusInput
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addChip();
          }
        }}
        placeholder={placeholder || "Type and press Enter"}
        style={{ fontSize: 13 }}
      />
    </div>
  );
}

function Spinner({ size = 20, color = GOLD }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ animation: "mad-spin 0.8s linear infinite", display: "block" }}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke={color}
        strokeWidth="3"
        fill="none"
        strokeDasharray="50 20"
        strokeLinecap="round"
      />
      <style>{`@keyframes mad-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Step 1 -- Brand Identity
// ---------------------------------------------------------------------------
function Step1({ data, setField }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <label style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 6, display: "block" }}>
          Brand Name <span style={{ color: ERROR_RED }}>*</span>
        </label>
        <FocusInput
          value={data.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder="e.g., Acme Corp"
          style={{ fontSize: 16, fontWeight: 600, padding: "12px 16px" }}
          autoFocus
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <label style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 6, display: "block" }}>
            Industry
          </label>
          <FocusInput
            value={data.industry}
            onChange={(e) => setField("industry", e.target.value)}
            placeholder="e.g., Banking & Financial Services"
          />
        </div>
        <div>
          <label style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 6, display: "block" }}>
            Country
          </label>
          <FocusInput
            value={data.country}
            onChange={(e) => setField("country", e.target.value)}
            placeholder="e.g., Romania"
          />
        </div>
      </div>

      <div>
        <label style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 6, display: "block" }}>
          Website URL
        </label>
        <FocusInput
          value={data.website_url}
          onChange={(e) => setField("website_url", e.target.value)}
          placeholder="https://www.example.com"
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 24,
          padding: "14px 16px",
          background: "#f9fafb",
          borderRadius: 12,
          marginTop: 4,
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={data.enable_llm}
            onChange={(e) => setField("enable_llm", e.target.checked)}
            style={{ width: 16, height: 16, accentColor: GOLD }}
          />
          Enable in LLM Discovery
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={data.enable_ads}
            onChange={(e) => setField("enable_ads", e.target.checked)}
            style={{ width: 16, height: 16, accentColor: GOLD }}
          />
          Enable in ADS Discovery
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 -- Brand Hierarchy
// ---------------------------------------------------------------------------
function Step2({ data, setField }) {
  const addSubBrand = () => {
    setField("sub_brands", [...data.sub_brands, { name: "", aliases: [] }]);
  };

  const removeSubBrand = (idx) => {
    setField("sub_brands", data.sub_brands.filter((_, i) => i !== idx));
  };

  const updateSubBrand = (idx, key, val) => {
    const updated = data.sub_brands.map((sb, i) => (i === idx ? { ...sb, [key]: val } : sb));
    setField("sub_brands", updated);
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 24 }}>
      {/* Left: inputs */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div
          style={{
            padding: "10px 14px",
            background: "#f9fafb",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            color: NAVY,
          }}
        >
          Master Brand:{" "}
          <span style={{ fontWeight: 800 }}>{data.name || "..."}</span>
        </div>

        <div>
          <label style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 6, display: "block" }}>
            Master Brand Aliases
          </label>
          <ChipInput
            values={data.master_aliases}
            onChange={(v) => setField("master_aliases", v)}
            placeholder="Type alias and press Enter"
          />
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <label style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>Sub-Brands</label>
            <button
              type="button"
              onClick={addSubBrand}
              style={{
                ...btnBase,
                padding: "6px 14px",
                fontSize: 13,
                background: CHIP_BG,
                color: CHIP_TEXT,
                fontWeight: 700,
              }}
            >
              + Add Sub-Brand
            </button>
          </div>
          {data.sub_brands.length === 0 && (
            <div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>No sub-brands added yet.</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {data.sub_brands.map((sb, idx) => (
              <div
                key={idx}
                style={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: 12,
                  padding: 14,
                  background: "#fafbfc",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <FocusInput
                    value={sb.name}
                    onChange={(e) => updateSubBrand(idx, "name", e.target.value)}
                    placeholder="Sub-brand name"
                    style={{ flex: 1, fontSize: 13, fontWeight: 600 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeSubBrand(idx)}
                    style={{
                      background: "none",
                      border: "none",
                      color: ERROR_RED,
                      cursor: "pointer",
                      fontSize: 18,
                      fontWeight: 700,
                      lineHeight: 1,
                      padding: "4px 8px",
                    }}
                    title="Remove sub-brand"
                  >
                    &times;
                  </button>
                </div>
                <ChipInput
                  values={sb.aliases}
                  onChange={(v) => updateSubBrand(idx, "aliases", v)}
                  placeholder="Aliases (type + Enter)"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: tree preview */}
      <div
        style={{
          background: "#f9fafb",
          borderRadius: 12,
          padding: 16,
          fontSize: 13,
          lineHeight: 1.8,
          color: NAVY,
          alignSelf: "start",
          position: "sticky",
          top: 0,
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "#6b7280" }}>
          Hierarchy Preview
        </div>
        <div style={{ fontWeight: 700 }}>
          {data.name || "Brand Name"}
        </div>
        {data.master_aliases.length > 0 && (
          <div style={{ color: "#6b7280", fontSize: 12, marginLeft: 12 }}>
            aka: {data.master_aliases.join(", ")}
          </div>
        )}
        {data.sub_brands
          .filter((sb) => sb.name.trim())
          .map((sb, i) => (
            <div key={i} style={{ marginLeft: 16 }}>
              <span style={{ color: "#9ca3af", marginRight: 6 }}>|--</span>
              <span style={{ fontWeight: 600 }}>{sb.name}</span>
              {sb.aliases.length > 0 && (
                <div style={{ color: "#6b7280", fontSize: 12, marginLeft: 28 }}>
                  aka: {sb.aliases.join(", ")}
                </div>
              )}
            </div>
          ))}
        {data.sub_brands.filter((sb) => sb.name.trim()).length === 0 && (
          <div style={{ color: "#c4c9d2", fontSize: 12, marginLeft: 16, fontStyle: "italic" }}>
            No sub-brands
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 -- Competition
// ---------------------------------------------------------------------------
function Step3({ data, setField }) {
  const addCompetitor = () => {
    setField("competitors", [...data.competitors, { name: "", sub_brands: [], _expanded: false }]);
  };

  const removeCompetitor = (idx) => {
    setField("competitors", data.competitors.filter((_, i) => i !== idx));
  };

  const updateCompetitor = (idx, key, val) => {
    const updated = data.competitors.map((c, i) => (i === idx ? { ...c, [key]: val } : c));
    setField("competitors", updated);
  };

  const addCompetitorSubBrand = (idx) => {
    const updated = data.competitors.map((c, i) =>
      i === idx ? { ...c, sub_brands: [...c.sub_brands, ""], _expanded: true } : c
    );
    setField("competitors", updated);
  };

  const updateCompetitorSubBrand = (compIdx, sbIdx, val) => {
    const updated = data.competitors.map((c, i) => {
      if (i !== compIdx) return c;
      const sbs = c.sub_brands.map((s, j) => (j === sbIdx ? val : s));
      return { ...c, sub_brands: sbs };
    });
    setField("competitors", updated);
  };

  const removeCompetitorSubBrand = (compIdx, sbIdx) => {
    const updated = data.competitors.map((c, i) => {
      if (i !== compIdx) return c;
      return { ...c, sub_brands: c.sub_brands.filter((_, j) => j !== sbIdx) };
    });
    setField("competitors", updated);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <label style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>Competitors</label>
        <button
          type="button"
          onClick={addCompetitor}
          style={{
            ...btnBase,
            padding: "6px 14px",
            fontSize: 13,
            background: CHIP_BG,
            color: CHIP_TEXT,
            fontWeight: 700,
          }}
        >
          + Add Competitor
        </button>
      </div>

      {data.competitors.length === 0 && (
        <div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>
          No competitors added yet. You can skip this step and add them later.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {data.competitors.map((comp, idx) => (
          <div
            key={idx}
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              padding: 14,
              background: "#fafbfc",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FocusInput
                value={comp.name}
                onChange={(e) => updateCompetitor(idx, "name", e.target.value)}
                placeholder="Competitor name"
                style={{ flex: 1, fontSize: 14, fontWeight: 600 }}
              />
              <button
                type="button"
                onClick={() =>
                  updateCompetitor(idx, "_expanded", !comp._expanded)
                }
                style={{
                  ...btnBase,
                  padding: "6px 10px",
                  fontSize: 12,
                  background: comp._expanded ? "#e5e7eb" : "#f3f4f6",
                  color: "#6b7280",
                }}
              >
                {comp._expanded ? "Hide" : "Sub-brands"}
              </button>
              <button
                type="button"
                onClick={() => removeCompetitor(idx)}
                style={{
                  background: "none",
                  border: "none",
                  color: ERROR_RED,
                  cursor: "pointer",
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1,
                  padding: "4px 8px",
                }}
                title="Remove competitor"
              >
                &times;
              </button>
            </div>

            {comp._expanded && (
              <div style={{ marginTop: 10, paddingLeft: 12 }}>
                {comp.sub_brands.map((sb, sbIdx) => (
                  <div key={sbIdx} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <FocusInput
                      value={sb}
                      onChange={(e) => updateCompetitorSubBrand(idx, sbIdx, e.target.value)}
                      placeholder="Sub-brand name"
                      style={{ flex: 1, fontSize: 13 }}
                    />
                    <button
                      type="button"
                      onClick={() => removeCompetitorSubBrand(idx, sbIdx)}
                      style={{
                        background: "none",
                        border: "none",
                        color: CHIP_X,
                        cursor: "pointer",
                        fontSize: 16,
                        fontWeight: 700,
                        padding: "2px 6px",
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addCompetitorSubBrand(idx)}
                  style={{
                    ...btnGhost,
                    padding: "4px 0",
                    fontSize: 12,
                  }}
                >
                  + Add sub-brand
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 -- Owned Domains
// ---------------------------------------------------------------------------
function Step4({ brandId, brandName, creating, createError, suggestions, detectLoading, detectError, selectedDomains, setSelectedDomains, manualDomains, setManualDomains }) {
  const [manualInput, setManualInput] = useState("");

  const toggleSuggestion = (domain) => {
    setSelectedDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    );
  };

  const addManual = () => {
    const trimmed = manualInput.trim().toLowerCase();
    if (!trimmed) return;
    if (manualDomains.includes(trimmed) || selectedDomains.includes(trimmed)) {
      setManualInput("");
      return;
    }
    setManualDomains((prev) => [...prev, trimmed]);
    setManualInput("");
  };

  const removeManual = (d) => {
    setManualDomains((prev) => prev.filter((x) => x !== d));
  };

  // Still creating the brand
  if (creating) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "32px 0" }}>
        <Spinner size={28} />
        <div style={{ fontSize: 14, color: "#6b7280" }}>Creating brand...</div>
      </div>
    );
  }

  if (createError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "24px 0" }}>
        <div style={{ color: ERROR_RED, fontWeight: 700, fontSize: 14 }}>Failed to create brand</div>
        <div style={{ color: "#6b7280", fontSize: 13, textAlign: "center", maxWidth: 400 }}>{createError}</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ fontSize: 14, color: "#6b7280" }}>
        Domains you own help us identify self-citations in LLM audit results for{" "}
        <strong style={{ color: NAVY }}>{brandName}</strong>.
      </div>

      {/* Suggestions */}
      {detectLoading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 0" }}>
          <Spinner size={18} />
          <span style={{ fontSize: 13, color: "#6b7280" }}>Detecting owned domains...</span>
        </div>
      ) : detectError ? (
        <div style={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>{detectError}</div>
      ) : suggestions.length > 0 ? (
        <div>
          <label style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 8, display: "block" }}>
            Suggested Domains
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {suggestions.map((s) => {
              const checked = selectedDomains.includes(s.domain);
              return (
                <label
                  key={s.domain}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    borderRadius: 10,
                    border: `1px solid ${checked ? GOLD : BORDER}`,
                    background: checked ? "#fffdf0" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSuggestion(s.domain)}
                    style={{ width: 16, height: 16, accentColor: GOLD }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14, color: NAVY, flex: 1 }}>{s.domain}</span>
                  {s.source && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 6,
                        background: s.source === "pattern" ? BADGE_GREEN : BADGE_PURPLE,
                        color: s.source === "pattern" ? BADGE_GREEN_TEXT : BADGE_PURPLE_TEXT,
                      }}
                    >
                      {s.source}
                    </span>
                  )}
                  {s.reason && (
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>{s.reason}</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>
          No domain suggestions found. You can add domains manually below.
        </div>
      )}

      {/* Manual domains */}
      {manualDomains.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {manualDomains.map((d) => (
            <span
              key={d}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: CHIP_BG,
                color: CHIP_TEXT,
                fontSize: 13,
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: 8,
              }}
            >
              {d}
              <button
                type="button"
                onClick={() => removeManual(d)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: CHIP_X,
                  fontSize: 15,
                  lineHeight: 1,
                  padding: 0,
                  marginLeft: 2,
                  fontWeight: 700,
                }}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      <div>
        <label style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 6, display: "block" }}>
          Add Domain Manually
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <FocusInput
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addManual();
              }
            }}
            placeholder="e.g., acmecorp.com"
            style={{ flex: 1, fontSize: 13 }}
          />
          <button
            type="button"
            onClick={addManual}
            disabled={!manualInput.trim()}
            style={{
              ...btnSecondary,
              padding: "8px 16px",
              fontSize: 13,
              opacity: manualInput.trim() ? 1 : 0.5,
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal component
// ---------------------------------------------------------------------------
export default function BrandOnboardingModal({
  open,
  onClose,
  onCreated,
  currentUser,
  defaultEnableLlm = true,
  defaultEnableAds = false,
  editBrandId = null,  // if set, opens in edit mode with existing data
}) {
  const isEditMode = !!editBrandId;
  // Step state
  const [step, setStep] = useState(0);

  // Form data (steps 1-3)
  const [formData, setFormData] = useState({
    name: "",
    industry: "",
    country: "",
    website_url: "",
    enable_llm: defaultEnableLlm,
    enable_ads: defaultEnableAds,
    master_aliases: [],
    sub_brands: [],
    competitors: [],
  });

  // Step 4 state
  const [brandId, setBrandId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [detectLoading, setDetectLoading] = useState(false);
  const [detectError, setDetectError] = useState(null);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [manualDomains, setManualDomains] = useState([]);
  const [finishing, setFinishing] = useState(false);

  const createdBrandRef = useRef(null);

  const [editLoading, setEditLoading] = useState(false);

  // Reset on open/close — load existing data in edit mode
  useEffect(() => {
    if (open) {
      setStep(0);
      setCreating(false);
      setStep4Done(false);
      setCreateError(null);
      setSuggestions([]);
      setDetectLoading(false);
      setDetectError(null);
      setSelectedDomains([]);
      setManualDomains([]);
      setFinishing(false);
      createdBrandRef.current = null;

      if (editBrandId) {
        // Edit mode: load existing data
        setBrandId(editBrandId);
        setEditLoading(true);
        brandGetFullDetails(editBrandId)
          .then((data) => {
            setFormData({
              name: data.name || "",
              industry: data.industry || "",
              country: data.country || "",
              website_url: data.website_url || "",
              enable_llm: data.modules?.llm ?? defaultEnableLlm,
              enable_ads: data.modules?.ads ?? defaultEnableAds,
              master_aliases: data.master_aliases || [],
              sub_brands: (data.sub_brands || []).map((s) => ({
                name: s.name,
                aliases: s.aliases || [],
              })),
              competitors: (data.competitors || []).map((c) => ({
                name: c.name,
                sub_brands: c.sub_brands || [],
              })),
            });
            setSelectedDomains(data.owned_domains || []);
          })
          .catch(() => {})
          .finally(() => setEditLoading(false));
      } else {
        // Create mode: reset
        setBrandId(null);
        setFormData({
          name: "",
          industry: "",
          country: "",
          website_url: "",
          enable_llm: defaultEnableLlm,
          enable_ads: defaultEnableAds,
          master_aliases: [],
          sub_brands: [],
          competitors: [],
        });
      }
    }
  }, [open, editBrandId, defaultEnableLlm, defaultEnableAds]);

  const setField = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const [step4Done, setStep4Done] = useState(false);
  const formDataRef = useRef(formData);
  const selectedDomainsRef = useRef(selectedDomains);
  formDataRef.current = formData;
  selectedDomainsRef.current = selectedDomains;

  // Trigger step 4 processing when step changes to 3
  useEffect(() => {
    if (step !== 3 || step4Done) return;

    const fd = formDataRef.current;
    const sd = selectedDomainsRef.current;

    // In edit mode, brand already exists — save updates then detect
    if (isEditMode && brandId) {
      let active = true;
      (async () => {
        setCreating(true);
        setCreateError(null);
        try {
          await brandUpdateFullDetails(brandId, {
            name: fd.name.trim(),
            industry: fd.industry.trim() || null,
            country: fd.country.trim() || null,
            website_url: fd.website_url.trim() || null,
            master_aliases: fd.master_aliases,
            sub_brands: fd.sub_brands.filter((sb) => sb.name.trim()).map((sb) => ({ name: sb.name.trim(), aliases: sb.aliases })),
            competitors: fd.competitors.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), sub_brands: (c.sub_brands || []).filter((s) => s.trim()) })),
            owned_domains: sd,
          });
          if (!active) return;
          setCreating(false);

          setDetectLoading(true);
          try {
            const detectResult = await brandAutoDetectDomains(brandId);
            if (!active) return;
            const sug = detectResult?.suggestions || [];
            setSuggestions(sug);
            const existing = new Set(sd);
            sug.forEach((s) => existing.add(s.domain));
            setSelectedDomains([...existing]);
          } catch {
            if (active) setDetectError("Could not auto-detect domains. You can add them manually.");
          } finally {
            if (active) setDetectLoading(false);
          }
          if (active) setStep4Done(true);
        } catch (err) {
          if (active) { setCreateError(err?.message || "Failed to update brand."); setCreating(false); }
        }
      })();
      return () => { active = false; };
    }

    // Create mode
    if (brandId) return;

    let active = true;
    (async () => {
      setCreating(true);
      setCreateError(null);

      try {
        const payload = {
          name: fd.name.trim(),
          industry: fd.industry.trim() || undefined,
          country: fd.country.trim() || undefined,
          website_url: fd.website_url.trim() || undefined,
          sub_brands: fd.sub_brands
            .filter((sb) => sb.name.trim())
            .map((sb) => ({ name: sb.name.trim(), aliases: sb.aliases })),
          master_aliases: fd.master_aliases,
          competitors: fd.competitors
            .filter((c) => c.name.trim())
            .map((c) => ({
              name: c.name.trim(),
              sub_brands: c.sub_brands.filter((s) => s.trim()),
            })),
          owned_domains: [],
          enable_llm: fd.enable_llm,
          enable_ads: fd.enable_ads,
        };

        const result = await brandOnboard(payload);
        if (!active) return;

        const newBrandId = result?.id || result?.brand_id;
        if (!newBrandId) throw new Error("Brand created but no ID returned.");

        setBrandId(newBrandId);
        createdBrandRef.current = result;
        setCreating(false);

        setDetectLoading(true);
        try {
          const detectResult = await brandAutoDetectDomains(newBrandId);
          if (!active) return;
          const sug = detectResult?.suggestions || [];
          setSuggestions(sug);
          setSelectedDomains(sug.map((s) => s.domain));
        } catch (err) {
          if (active) setDetectError("Could not auto-detect domains. You can add them manually.");
        } finally {
          if (active) { setDetectLoading(false); setStep4Done(true); }
        }
      } catch (err) {
        if (active) {
          setCreating(false);
          setCreateError(err?.message || "Failed to create brand.");
        }
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, step4Done]);

  // Finish: save owned domains then close
  const handleFinish = useCallback(async () => {
    if (!brandId) return;

    const allDomains = [...new Set([...selectedDomains, ...manualDomains])];

    setFinishing(true);
    try {
      // Save each domain
      await Promise.all(
        allDomains.map((domain) => citationsAddOwnedDomain(brandId, domain))
      );
    } catch {
      // Non-fatal; domains can be added later
    }
    setFinishing(false);

    onCreated?.(createdBrandRef.current || { id: brandId, name: formData.name });
    onClose?.();
  }, [brandId, selectedDomains, manualDomains, formData.name, onCreated, onClose]);

  // Navigation
  const canNext = step === 0 ? formData.name.trim().length > 0 : true;

  const goNext = () => {
    if (step < TOTAL_STEPS - 1) setStep(step + 1);
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={cardStyle}>
        {/* Header */}
        <div
          style={{
            padding: "20px 28px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: NAVY }}>
            {isEditMode ? "Edit Brand Setup" : "Brand Onboarding"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              color: "#9ca3af",
              cursor: "pointer",
              padding: "4px 8px",
              lineHeight: 1,
              fontWeight: 700,
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} />

        {/* Divider */}
        <div style={{ height: 1, background: BORDER, margin: "0 28px" }} />

        {/* Body (scrollable) */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
          {step === 0 && <Step1 data={formData} setField={setField} />}
          {step === 1 && <Step2 data={formData} setField={setField} />}
          {step === 2 && <Step3 data={formData} setField={setField} />}
          {step === 3 && (
            <Step4
              brandId={brandId}
              brandName={formData.name}
              creating={creating}
              createError={createError}
              suggestions={suggestions}
              detectLoading={detectLoading}
              detectError={detectError}
              selectedDomains={selectedDomains}
              setSelectedDomains={setSelectedDomains}
              manualDomains={manualDomains}
              setManualDomains={setManualDomains}
            />
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: BORDER, margin: "0 28px" }} />

        {/* Footer */}
        <div
          style={{
            padding: "16px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            {step > 0 && step < 3 && (
              <button type="button" onClick={goBack} style={btnSecondary}>
                Back
              </button>
            )}
            {step === 3 && !creating && createError && (
              <button
                type="button"
                onClick={() => {
                  setCreateError(null);
                  setCreating(false);
                  setBrandId(null);
                }}
                style={btnSecondary}
              >
                Retry
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {(step === 1 || step === 2) && (
              <button type="button" onClick={goNext} style={btnGhost}>
                Skip
              </button>
            )}
            {step < 3 && (
              <button
                type="button"
                onClick={goNext}
                disabled={!canNext}
                style={{
                  ...btnPrimary,
                  opacity: canNext ? 1 : 0.45,
                  cursor: canNext ? "pointer" : "not-allowed",
                }}
                onMouseEnter={(e) => {
                  if (canNext) e.currentTarget.style.background = GOLD_HOVER;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = GOLD;
                }}
              >
                Next
              </button>
            )}
            {step === 3 && brandId && !creating && (
              <button
                type="button"
                onClick={handleFinish}
                disabled={finishing}
                style={{
                  ...btnPrimary,
                  opacity: finishing ? 0.6 : 1,
                  minWidth: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (!finishing) e.currentTarget.style.background = GOLD_HOVER;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = GOLD;
                }}
              >
                {finishing && <Spinner size={14} color={NAVY} />}
                {finishing ? "Saving..." : "Finish"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
